import { Component } from "@theme/component";
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
} from "@theme/events";
import { prefersReducedMotion } from "@theme/utilities";

/** Pointer travel, in pixels, before a press on the carousel row counts as a drag rather than a click. */
const DRAG_THRESHOLD = 5;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [mediaCarouselTrack] - The scrolling row of media below the grid.
 * @property {HTMLButtonElement} [mediaCarouselPrevious] - The carousel's previous arrow.
 * @property {HTMLButtonElement} [mediaCarouselNext] - The carousel's next arrow.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest(".shopify-section, dialog");

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal },
    );
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      {
        signal,
      },
    );

    const { mediaCarouselTrack } = this.refs;
    if (mediaCarouselTrack) {
      const updateArrows = () => this.#updateMediaCarouselArrows();
      const resizeObserver = new ResizeObserver(updateArrows);

      mediaCarouselTrack.addEventListener("scroll", updateArrows, {
        passive: true,
        signal,
      });
      resizeObserver.observe(mediaCarouselTrack);
      signal.addEventListener("abort", () => resizeObserver.disconnect());

      // With no scrollbar, and no arrows when they're turned off, a mouse drags the row instead
      mediaCarouselTrack.addEventListener(
        "pointerdown",
        this.#handleCarouselPointerDown,
        { signal },
      );
      // Images are natively draggable, which would start a ghost drag instead of scrolling the row
      mediaCarouselTrack.addEventListener(
        "dragstart",
        (event) => event.preventDefault(),
        { signal },
      );
    }
  }

  #controller = new AbortController();

  /** @type {{ pointerId: number, startX: number, scrollLeft: number, moved: boolean } | null} */
  #drag = null;
  #swallowClick = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#removeDragListeners();
    this.#drag = null;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector("media-gallery");

    if (!newMediaGallery) return;

    const currentMediaIds = this.#getPrimaryMediaIds(this);
    const nextMediaIds = this.#getPrimaryMediaIds(newMediaGallery);
    const hasSamePrimaryMedia =
      currentMediaIds.length > 0 &&
      currentMediaIds.length === nextMediaIds.length &&
      currentMediaIds.every(
        (mediaId, index) => mediaId === nextMediaIds[index],
      );

    // Avoid replacing the entire gallery when the selected variant points to the same media.
    // Replacing identical markup causes a visible image reload jitter on mobile.
    if (hasSamePrimaryMedia) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Collects media IDs from the primary gallery only (carousel/grid), excluding zoom dialog media.
   * @param {ParentNode} source
   * @returns {string[]}
   */
  #getPrimaryMediaIds(source) {
    return Array.from(
      source.querySelectorAll(
        "slideshow-slide .product-media[data-media-id], .media-gallery__grid .product-media[data-media-id], .media-gallery__carousel-track .product-media[data-media-id]",
      ),
    )
      .map((media) => media.dataset.mediaId)
      .filter((mediaId) => Boolean(mediaId));
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    // Letting go of a drag over a media clicks it. The theme binds `on:click` on document, ahead
    // of any listener here, so the guard has to live in the handler itself.
    if (this.#swallowClick) {
      this.#swallowClick = false;
      return;
    }

    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  /**
   * Scrolls the carousel below the grid by most of its visible width.
   *
   * @param {number} direction - 1 for next, -1 for previous.
   */
  scrollMediaCarousel(direction) {
    const { mediaCarouselTrack } = this.refs;
    if (!mediaCarouselTrack) return;

    const isRtl = getComputedStyle(mediaCarouselTrack).direction === "rtl";

    mediaCarouselTrack.scrollBy({
      left: direction * (isRtl ? -1 : 1) * mediaCarouselTrack.clientWidth * 0.8,
      behavior: prefersReducedMotion() ? "instant" : "smooth",
    });
  }

  /**
   * Disables each carousel arrow once the row can't scroll further that way.
   */
  #updateMediaCarouselArrows() {
    const { mediaCarouselTrack, mediaCarouselPrevious, mediaCarouselNext } =
      this.refs;
    if (!mediaCarouselTrack) return;

    // scrollLeft is negative in RTL, so compare distances from the start
    const scrolled = Math.abs(mediaCarouselTrack.scrollLeft);
    const maxScroll =
      mediaCarouselTrack.scrollWidth - mediaCarouselTrack.clientWidth;

    if (mediaCarouselPrevious) mediaCarouselPrevious.disabled = scrolled <= 1;
    if (mediaCarouselNext)
      mediaCarouselNext.disabled = scrolled >= maxScroll - 1;
  }

  /**
   * Starts a drag when a mouse or pen presses on a row that has somewhere to scroll. Touch is left
   * alone: the row already scrolls natively there, with momentum that driving `scrollLeft` by hand
   * would throw away.
   *
   * @param {PointerEvent} event
   */
  #handleCarouselPointerDown = (event) => {
    // A press always precedes a click, so a suppression left over from an earlier drag ends here
    this.#swallowClick = false;

    const { mediaCarouselTrack } = this.refs;
    if (!mediaCarouselTrack) return;
    if (event.pointerType === "touch" || event.button !== 0) return;
    if (mediaCarouselTrack.scrollWidth <= mediaCarouselTrack.clientWidth)
      return;

    this.#drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollLeft: mediaCarouselTrack.scrollLeft,
      moved: false,
    };

    // On the window, so a drag that wanders off the row, or off the page, still tracks
    window.addEventListener("pointermove", this.#handleCarouselDragMove);
    window.addEventListener("pointerup", this.#endCarouselDrag);
    window.addEventListener("pointercancel", this.#endCarouselDrag);
  };

  /** @param {PointerEvent} event */
  #handleCarouselDragMove = (event) => {
    const drag = this.#drag;
    const { mediaCarouselTrack } = this.refs;
    if (!drag || !mediaCarouselTrack || event.pointerId !== drag.pointerId)
      return;

    const distance = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(distance) < DRAG_THRESHOLD) return;

    if (!drag.moved) {
      drag.moved = true;
      mediaCarouselTrack.classList.add(
        "media-gallery__carousel-track--dragging",
      );
    }

    // Keeps the drag from turning into a text selection
    event.preventDefault();
    mediaCarouselTrack.scrollLeft = drag.scrollLeft - distance;
  };

  /** @param {PointerEvent} event */
  #endCarouselDrag = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    this.#removeDragListeners();
    // Snapping comes back on here, settling the row on the nearest media
    this.refs.mediaCarouselTrack?.classList.remove(
      "media-gallery__carousel-track--dragging",
    );
    this.#swallowClick = drag.moved;
    this.#drag = null;
  };

  #removeDragListeners() {
    window.removeEventListener("pointermove", this.#handleCarouselDragMove);
    window.removeEventListener("pointerup", this.#endCarouselDrag);
    window.removeEventListener("pointercancel", this.#endCarouselDrag);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  /**
   * The media on screen, in gallery order. Media that are full size on one breakpoint and in the
   * carousel below on the other are rendered twice, with one copy hidden, so skip the hidden ones.
   */
  get media() {
    return this.refs.media?.filter(
      (media) => media.getClientRects().length > 0,
    );
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
